import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * The highest cart-action sequence (see nextCartActionSequence()) whose
 * result has been painted onto any cart-icon bubble on the page. Two cart
 * requests can resolve out of order — e.g. the background re-sync fired
 * right after an add-to-cart can still be in flight when a quantity is
 * edited afterward, and can then resolve after that edit's own response.
 * Ignoring anything older than the highest sequence already applied means
 * the later-started action always wins, regardless of which response
 * actually arrives first.
 *
 * Deliberately module-level rather than a field on the component instance:
 * the header renders a `<cart-icon>` twice (the visible header plus a
 * hidden copy inside the mobile menu drawer), and a cart update morphs the
 * header section, which can tear down and recreate either `<cart-icon>`
 * element mid-flow. A per-instance field would lose its memory of the
 * highest sequence applied the moment that happened, letting a stale
 * response through again on the freshly (re)connected instance.
 * @type {number}
 */
let highestAppliedCartActionSequence = 0;

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /**
   * Deltas already painted on the bubble for add requests that are still in
   * flight, keyed by their CartPendingUpdateEvent token.
   * @type {Map<string, number>}
   */
  #pendingDeltas = new Map();

  /** @type {number} */
  get currentCartCount() {
    // Read the real count from data-count, not the displayed text: once the
    // cart crosses 100 items the bubble shows "99+" (see the setter below),
    // and parsing that text directly used to yield NaN — which poisoned
    // every later delta (`NaN + 1` is still NaN), leaving the bubble stuck
    // blank/wrong for the rest of the session even after the count dropped
    // back under 100.
    const raw = this.refs.cartBubbleCount.dataset.count ?? this.refs.cartBubbleCount.textContent;
    return parseInt(raw ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.dataset.count = String(value);
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener(ThemeEvents.cartPendingUpdate, this.onCartPendingUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener(ThemeEvents.cartPendingUpdate, this.onCartPendingUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Paints the added items on the bubble as soon as the request is sent, rather
   * than after the add response and the cart re-fetch that follows it.
   * @param {CustomEvent<{ delta: number, token: string }>} event - The pending update event.
   */
  onCartPendingUpdate = (event) => {
    const { delta, token } = event.detail;
    if (!delta) return;

    this.#pendingDeltas.set(token, delta);
    this.renderCartBubble(this.currentCartCount + delta, false);
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const { token, didError, sequence } = event.detail.data ?? {};

    // Discard a response from an action that started before one whose
    // result we've already painted — see highestAppliedCartActionSequence above.
    if (typeof sequence === 'number') {
      if (sequence < highestAppliedCartActionSequence) return;
      highestAppliedCartActionSequence = sequence;
    }

    const pendingDelta = token == null ? undefined : this.#pendingDeltas.get(token);

    // This add was already painted optimistically, so settle it instead of
    // counting it a second time.
    if (pendingDelta !== undefined) {
      this.#pendingDeltas.delete(token);

      const serverCount = event.detail.resource?.item_count;
      if (typeof serverCount === 'number') return this.renderCartBubble(serverCount, false, false);
      if (didError) return this.renderCartBubble(this.currentCartCount - pendingDelta, false, false);
      return;
    }

    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count
    const nextCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    const countChanged = nextCartCount !== this.currentCartCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = nextCartCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    // A reconciliation that lands on the count already shown shouldn't re-pop
    // the bubble.
    if (!animate || !countChanged || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
